import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Key, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';
import PaymentMethodLogo from './PaymentMethodLogo';
import usePaymentMethodLabel from './usePaymentMethodLabel';
import { PaymentMethod, useSpacePaymentMethods } from './usePaymentMethods';

interface PaymentMethodSelectionProps {
    spaceId?: Key;
    className?: string;
    /** Request in flight — locks the list so the choice can't change mid-booking */
    isLoading?: boolean;
    /** Method a booking rule pins: shown as the only, locked choice */
    forcedMethod?: string;
}

/**
 * Picks the method the booking is charged with. The order button that carries the
 * § 312j Abs. 3 BGB payment-obligation wording sits in the booking rail, so this list
 * only ever selects — pressing a row never books.
 *
 * Leaving it untouched is a valid choice: the booking then goes out without a method
 * and Mollie asks for one at checkout.
 *
 * Renders nothing when a saved mandate is used (the mandate's own method applies) or
 * when Mollie reports no usable method.
 */
export default function PaymentMethodSelection({
    spaceId,
    className,
    isLoading,
    forcedMethod
}: PaymentMethodSelectionProps) {
    const { t } = useTranslation('general');
    const { watch, setValue } = useFormContext();
    const mandateId = watch('mollie_connect_mandate_id');
    const selected = watch('payment_method');
    const { label, hint } = usePaymentMethodLabel();

    // the mandate picker is hidden then, but a preselected mandate would still be charged
    useEffect(() => {
        if (!forcedMethod) return;
        setValue('mollie_connect_mandate_id', undefined);
        setValue('mollie_connect_create_mandate', false);
    }, [forcedMethod, setValue]);

    // a mandate charges its own method, so offering a choice here would be misleading
    const { data } = useSpacePaymentMethods(spaceId, !mandateId || !!forcedMethod);
    const available: PaymentMethod[] = data?.data ?? [];
    // the space may not offer the forced method itself, the booker still has to see how they pay
    const methods: PaymentMethod[] = forcedMethod
        ? [
              available.find((method) => method.id === forcedMethod) ?? {
                  id: forcedMethod,
                  description: forcedMethod,
                  image: null
              }
          ]
        : available;

    if (!forcedMethod && (mandateId || methods.length === 0)) return null;

    const handleSelect = (method: PaymentMethod) => {
        if (isLoading || forcedMethod) return;
        // pressing the selected row again clears it, back to "let Mollie ask"
        setValue('payment_method', selected === method.id ? undefined : method.id);
    };

    return (
        <div className={className}>
            <span className="tw-mb-2 tw-block tw-text-sm tw-font-medium tw-text-accents-8">
                {t(forcedMethod ? 'mollie.paymentMethods.forcedTitle' : 'mollie.paymentMethods.selectTitle')}
            </span>

            <div className="tw-flex tw-flex-col tw-gap-2">
                {methods.map((method) => {
                    const isSelected = forcedMethod ? true : selected === method.id;
                    const subtitle = hint(method);

                    return (
                        <div
                            key={method.id}
                            role="radio"
                            aria-checked={isSelected}
                            aria-disabled={!!forcedMethod}
                            tabIndex={0}
                            onClick={() => handleSelect(method)}
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                event.preventDefault();
                                handleSelect(method);
                            }}
                            className={cn(
                                'tw-flex tw-cursor-pointer tw-items-center tw-gap-4 tw-rounded-xl tw-border-2 tw-border-solid tw-bg-white tw-p-3 tw-transition-colors',
                                isSelected
                                    ? 'tw-border-success'
                                    : 'tw-border-accents-2 hover:tw-border-accents-5 hover:tw-bg-accents-0',
                                forcedMethod && 'tw-cursor-default',
                                isLoading && 'tw-pointer-events-none tw-opacity-60'
                            )}
                        >
                            <PaymentMethodLogo method={method} />
                            <div className="tw-min-w-0 tw-flex-1">
                                <span className="tw-block tw-font-semibold tw-text-accents-9">{label(method)}</span>
                                {subtitle && (
                                    <span className="tw-block tw-truncate tw-text-sm tw-text-accents-6">
                                        {subtitle}
                                    </span>
                                )}
                            </div>
                            <span
                                className={cn(
                                    'tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid',
                                    isSelected
                                        ? 'tw-border-success tw-bg-success tw-text-white'
                                        : 'tw-border-accents-5 tw-bg-white'
                                )}
                            >
                                {isSelected && <FontAwesomeIcon icon={faCheck} className="tw-text-[10px]" />}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
